/**
 * Stage Finance assumptions — timing and financing mechanics only.
 * Economics come from normalizePnl(outputs); never duplicate P&L amounts here.
 */

import { clamp, clampInt, n } from './math.js';
import {
  DEFAULT_CONSTRUCTION_PAYMENT_TRANCHES,
  hydratePaymentTiming,
  linkConstructionPaymentTranches,
  normalizeConstructionPaymentTranches,
} from './paymentTiming.js';
import {
  normalizeStrictSaleOffsets,
  programmeBoundsFromDurations,
  effectiveLandAcquisitionMonth,
} from './timingControls.js';

export const MODES = {
  venteATerme: 'venteATerme',
  quotePart: 'quotePart',
};

export {
  DEFAULT_CONSTRUCTION_PAYMENT_TRANCHES,
  linkConstructionPaymentTranches,
  normalizeConstructionPaymentTranches,
};

export {
  linkSaleMonthOffsets,
  linkLandAcquisitionMonth,
  programmeBoundsFromDurations,
  normalizeStrictSaleOffsets,
  saleCalendarMonth,
  isThreeSaleWindowFeasible,
  THREE_SALE_WINDOW_INCOMPLETE_REASON,
} from './timingControls.js';

/**
 * Sales allocation = how much is sold in each phase (must sum to 100%).
 * Sales phase timing = when each phase ends (offsets from authorization).
 * These are separate: allocation drives amounts; timing drives calendar months.
 *
 * Payment timing (land deposit, construction tranches, fee/feasibility spread)
 * is Stage-Finance-only — it never changes P&L totals.
 */
export const DEFAULT_ASSUMPTIONS = {
  mode: MODES.venteATerme,
  prepMonths: 6,
  authMonths: 6,
  constructionMonths: 18,
  deliveryBufferMonths: 2,
  salesAllocation: {
    preBank: 0.5,
    postBank: 0.3,
    late: 0.2,
  },
  /** Phase end offsets from authorization month. No percentages here. */
  salesPhaseTiming: {
    preBankEndOffsetFromAuthorization: 6,
    postBankEndOffsetFromAuthorization: 12,
    lateEndOffsetFromAuthorization: 18,
  },
  /** Presale % required before bank draws. Explicit — never "sales > 0". */
  bankActivationThreshold: 0.5,
  minCashBuffer: 150_000,
  depositRate: 0.1,
  /** VAT: deposits restricted unless this is > 0. */
  depositUsablePct: 0,
  /** Explicit completion distribution of capital + profit. Default: cash stays in SPV. */
  distributeAtCompletion: false,
  /** Explicit post-commencement SPV raises (never auto-invented). */
  scheduledSpvFunding: [],
  /**
   * Absolute land-acquisition month (1-indexed). null = end of preparation
   * (not hard-coded to permit month).
   */
  landAcquisitionMonth: null,
  /** When true, land facility also requires rental self-sufficiency. */
  landRequiresRentalSelfSufficiency: false,
  /** Scenario flag used only when landRequiresRentalSelfSufficiency is true. */
  rentalSelfSufficient: true,
  /** Share of land price paid at signing (default 0%). Remainder at acquisition. */
  landDepositPct: 0,
  /** Signing month for the land deposit (1-indexed). */
  landSigningMonth: 1,
  /** Months after authorization before construction payments start. */
  constructionStartOffsetFromAuth: 2,
  /** Five construction payment shares (Build + Reserve), must total 100%. */
  constructionPaymentTranches: [...DEFAULT_CONSTRUCTION_PAYMENT_TRANCHES],
};

export function normalizeSalesAllocation(raw = {}) {
  let pre = Math.max(0, n(raw.preBank ?? raw.preSales));
  let post = Math.max(0, n(raw.postBank));
  let late = Math.max(0, n(raw.late ?? raw.lateSales));
  const sum = pre + post + late;
  if (sum <= 0) return { preBank: 0.5, postBank: 0.3, late: 0.2 };
  return { preBank: pre / sum, postBank: post / sum, late: late / sum };
}

function hydrateSalesPhaseTiming(raw = {}, bounds = null) {
  // Prefer explicit timing. Legacy salesMilestones contribute offsets only (pct ignored).
  const legacy = Array.isArray(raw.salesMilestones) ? raw.salesMilestones : null;
  const legacyOffsets = legacy
    ? legacy
      .map((m) => clampInt(m?.offsetFromAuthorization ?? m?.offsetFromPermit, 0, 120, null))
      .filter((x) => x != null)
      .sort((a, b) => a - b)
    : [];

  const fromLegacy = (idx, fallback) => (
    legacyOffsets[idx] != null ? legacyOffsets[idx] : fallback
  );

  const t = raw.salesPhaseTiming && typeof raw.salesPhaseTiming === 'object'
    ? raw.salesPhaseTiming
    : {};

  const draft = {
    preBankEndOffsetFromAuthorization: clampInt(
      t.preBankEndOffsetFromAuthorization ?? fromLegacy(0, DEFAULT_ASSUMPTIONS.salesPhaseTiming.preBankEndOffsetFromAuthorization),
      0,
      120,
      DEFAULT_ASSUMPTIONS.salesPhaseTiming.preBankEndOffsetFromAuthorization,
    ),
    postBankEndOffsetFromAuthorization: clampInt(
      t.postBankEndOffsetFromAuthorization ?? fromLegacy(1, DEFAULT_ASSUMPTIONS.salesPhaseTiming.postBankEndOffsetFromAuthorization),
      0,
      120,
      DEFAULT_ASSUMPTIONS.salesPhaseTiming.postBankEndOffsetFromAuthorization,
    ),
    lateEndOffsetFromAuthorization: clampInt(
      t.lateEndOffsetFromAuthorization ?? fromLegacy(2, DEFAULT_ASSUMPTIONS.salesPhaseTiming.lateEndOffsetFromAuthorization),
      0,
      120,
      DEFAULT_ASSUMPTIONS.salesPhaseTiming.lateEndOffsetFromAuthorization,
    ),
  };

  // Strict pre < post < late within programme — never silently merge equals.
  if (bounds) {
    const strict = normalizeStrictSaleOffsets(draft, bounds);
    return {
      preBankEndOffsetFromAuthorization: strict.preBankEndOffsetFromAuthorization,
      postBankEndOffsetFromAuthorization: strict.postBankEndOffsetFromAuthorization,
      lateEndOffsetFromAuthorization: strict.lateEndOffsetFromAuthorization,
    };
  }
  // Without bounds (unit helpers), still forbid equals by cascading +1.
  let pre = draft.preBankEndOffsetFromAuthorization;
  let post = draft.postBankEndOffsetFromAuthorization;
  let late = draft.lateEndOffsetFromAuthorization;
  if (post <= pre) post = pre + 1;
  if (late <= post) late = post + 1;
  return {
    preBankEndOffsetFromAuthorization: pre,
    postBankEndOffsetFromAuthorization: post,
    lateEndOffsetFromAuthorization: late,
  };
}


/**
 * Adjust one sales-allocation share and rebalance the other two so the displayed
 * allocation always totals 100% (no silent engine-side renormalization surprise).
 */
export function linkSalesAllocation(current = {}, key, nextShare) {
  const keys = ['preBank', 'postBank', 'late'];
  if (!keys.includes(key)) return normalizeSalesAllocation(current);
  const clamped = Math.min(1, Math.max(0, Number(nextShare) || 0));
  const others = keys.filter((k) => k !== key);
  const remaining = Math.max(0, 1 - clamped);
  const otherSum = others.reduce((s, k) => s + Math.max(0, Number(current?.[k]) || 0), 0);
  const next = { preBank: 0, postBank: 0, late: 0, [key]: clamped };
  if (remaining <= 0) {
    others.forEach((k) => { next[k] = 0; });
  } else if (otherSum <= 1e-12) {
    const each = remaining / others.length;
    others.forEach((k) => { next[k] = each; });
  } else {
    others.forEach((k) => {
      next[k] = remaining * (Math.max(0, Number(current?.[k]) || 0) / otherSum);
    });
  }
  return normalizeSalesAllocation(next);
}


export function hydrateAssumptions(raw = {}) {
  const a = raw && typeof raw === 'object' ? raw : {};
  const mode = a.mode === MODES.quotePart ? MODES.quotePart : MODES.venteATerme;
  const paymentTiming = hydratePaymentTiming(a);

  const prepMonths = clampInt(a.prepMonths, 0, 60, DEFAULT_ASSUMPTIONS.prepMonths);
  const authMonths = clampInt(a.authMonths, 0, 60, DEFAULT_ASSUMPTIONS.authMonths);
  const constructionMonths = clampInt(a.constructionMonths, 1, 120, DEFAULT_ASSUMPTIONS.constructionMonths);
  const deliveryBufferMonths = clampInt(
    a.deliveryBufferMonths,
    0,
    36,
    DEFAULT_ASSUMPTIONS.deliveryBufferMonths,
  );

  const bounds = programmeBoundsFromDurations({
    prepMonths,
    authMonths,
    constructionMonths,
    deliveryBufferMonths,
    constructionStartOffsetFromAuth: paymentTiming.constructionStartOffsetFromAuth,
  });

  const salesPhaseTiming = hydrateSalesPhaseTiming(a, bounds);

  let landAcquisitionMonth = null;
  if (a.landAcquisitionMonth != null && a.landAcquisitionMonth !== '') {
    // Persist the effective month the engine uses — never a display-only 240.
    landAcquisitionMonth = effectiveLandAcquisitionMonth(a.landAcquisitionMonth, bounds);
  }

  return {
    mode,
    prepMonths,
    authMonths,
    constructionMonths,
    deliveryBufferMonths,
    salesAllocation: normalizeSalesAllocation(a.salesAllocation || DEFAULT_ASSUMPTIONS.salesAllocation),
    salesPhaseTiming,
    bankActivationThreshold: clamp(
      n(a.bankActivationThreshold ?? DEFAULT_ASSUMPTIONS.bankActivationThreshold),
      0,
      1,
    ),
    minCashBuffer: Math.max(0, n(a.minCashBuffer ?? DEFAULT_ASSUMPTIONS.minCashBuffer)),
    depositRate: clamp(n(a.depositRate ?? DEFAULT_ASSUMPTIONS.depositRate), 0, 0.5),
    depositUsablePct: clamp(n(a.depositUsablePct ?? DEFAULT_ASSUMPTIONS.depositUsablePct), 0, 1),
    distributeAtCompletion: Boolean(a.distributeAtCompletion),
    scheduledSpvFunding: Array.isArray(a.scheduledSpvFunding)
      ? a.scheduledSpvFunding
        .filter((row) => row && Number.isFinite(Number(row.month)) && Number.isFinite(Number(row.amount)))
        .map((row) => ({
          month: clampInt(row.month, 1, bounds.maxMonth, 1),
          amount: Math.max(0, n(row.amount)),
        }))
      : [],
    landAcquisitionMonth,
    landRequiresRentalSelfSufficiency: Boolean(a.landRequiresRentalSelfSufficiency),
    rentalSelfSufficient: a.rentalSelfSufficient == null ? true : Boolean(a.rentalSelfSufficient),
    ...paymentTiming,
  };
}
